/**
 * Selection state for proposal actions — the user's intent, and nothing else.
 *
 * This hook used to also own a dependency cascade: deselecting an action
 * deselected everything that "depended" on it, and re-selecting restored
 * them, minus a per-action memory recording which ones the user had turned
 * off themselves. All of that is gone.
 *
 * Cascading WROTE to the selection map, which destroyed the difference
 * between "the user turned this off" and "a cascade turned this off" — hence
 * the memory that existed to reconstruct it. Validity is now DERIVED from the
 * projected CRM state instead (see lib/proposals/utils/actionValidity.ts), so
 * nothing writes to the map except the user. `selected` is therefore pure
 * intent, re-selecting a create makes its dependents valid again for free,
 * and the memory has nothing left to protect.
 *
 * Disabled state and its reason are NOT here any more either: they depend on
 * the CRM store, so they are computed one level up in
 * `ProposalSelectionContext`, which has store access.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ProposedAction } from '../core/types';
import { reconcileSelectionState } from './reconcileSelectionState';

/** actionId → selected. Ids missing from the map read as selected. */
type SelectionMap = Map<number, boolean>;

/** Ids of the selected actions, in map insertion order. */
function selectedIdsOf(selection: SelectionMap): number[] {
  return [...selection.entries()]
    .filter(([, isSelected]) => isSelected)
    .map(([id]) => id);
}

interface UseActionSelectionResult {
  /** Check if an action is currently selected */
  isSelected: (actionId: number) => boolean;
  /** Toggle selection of an action, returns new selected IDs array */
  toggleAction: (actionId: number) => number[];
  /** Deselect multiple actions in a single state update */
  deselectActions: (actionIds: number[]) => void;
  /**
   * Select multiple actions in a single state update. Idempotent: an id that
   * is already selected is left alone — unlike toggling in a loop, which
   * would flip it straight back off.
   */
  selectActions: (actionIds: number[]) => void;
  /** Array of currently selected action IDs */
  selectedActionIds: number[];
  /** Reset selection to initial state from actions */
  resetSelection: () => void;
  /** Set selection from array of IDs */
  setSelection: (actionIds: number[]) => void;
  /** Select all actions */
  selectAll: () => void;
  /** Deselect all actions */
  deselectAll: () => void;
  /** Whether all actions are currently selected */
  isAllSelected: boolean;
  /** Whether some (but not all) actions are currently selected */
  isSomeSelected: boolean;
}

/**
 * Hook for managing action selection state
 *
 * @param actions - Array of proposed actions from the proposal
 * @returns Selection state and control functions
 *
 * @example
 * ```tsx
 * const { isSelected, toggleAction } = useActionSelection(proposal.actions);
 *
 * return (
 *   <div>
 *     {proposal.actions.map(action => (
 *       <input
 *         type="checkbox"
 *         checked={isSelected(action.id)}
 *         onChange={() => toggleAction(action.id)}
 *       />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useActionSelection(
  actions: ProposedAction[],
  nonSelectableActionIds: number[] = [],
  proposalId?: number
): UseActionSelectionResult {
  // IDs excluded from selectAll / isAllSelected / isSomeSelected (e.g. no-op actions)
  const nonSelectableSet = useMemo(
    () => new Set(nonSelectableActionIds),
    [nonSelectableActionIds]
  );

  // Actions eligible for select-all / selection-driven counts
  const selectableActions = useMemo(
    () => actions.filter(a => !nonSelectableSet.has(a.id)),
    [actions, nonSelectableSet]
  );

  // Build initial selection state from actions
  const initialSelection = useMemo(() => {
    const map = new Map<number, boolean>();
    for (const action of actions) {
      // Default to true if is_selected is undefined
      map.set(action.id, action.is_selected ?? true);
    }
    return map;
  }, [actions]);

  const [selection, setSelection] = useState<SelectionMap>(
    () => initialSelection
  );

  // Track previous action IDs to detect when versions change (IDs change)
  const prevActionsRef = useRef<
    { id: number; executionOrder: number; isSelected: boolean }[]
  >(
    actions.map(a => ({
      id: a.id,
      executionOrder: a.execution_order,
      isSelected: a.is_selected ?? true,
    }))
  );

  // Track the proposal this selection state belongs to, so a reused panel that
  // switches proposals resets rather than mapping the prior proposal's
  // selection onto the new one by execution_order (see reconcileSelectionState).
  const prevProposalIdRef = useRef(proposalId);

  // Sync selection state when action IDs change (e.g., after versioning creates new IDs)
  // or when new actions are added. Maps old selection state to new IDs by execution_order
  // (position) to preserve user intent. `reconcileSelectionState` is a stable
  // module-level import, not a value from component scope, so it is correctly
  // omitted from this effect's dependency array — this is standard
  // react-hooks/exhaustive-deps behavior for imported functions, not a
  // suppression, and this comment exists so a future contributor doesn't "fix"
  // a perceived missing dependency and break the fast-path optimization (the
  // one that preserves user intent when action IDs are unchanged).
  useEffect(() => {
    const prevActions = prevActionsRef.current;
    const proposalChanged =
      proposalId !== undefined && prevProposalIdRef.current !== proposalId;
    setSelection(prev => {
      const { nextSelection } = reconcileSelectionState(
        prevActions,
        actions,
        prev,
        proposalChanged
      );
      return nextSelection ?? prev;
    });
    prevActionsRef.current = actions.map(a => ({
      id: a.id,
      executionOrder: a.execution_order,
      isSelected: a.is_selected ?? true,
    }));
    prevProposalIdRef.current = proposalId;
  }, [actions, proposalId]);

  // Check if an action is selected
  const isSelected = useCallback(
    (actionId: number): boolean => {
      return selection.get(actionId) ?? true;
    },
    [selection]
  );

  /** Flip one id in a selection map, returning a new map. */
  const withToggled = useCallback(
    (prev: SelectionMap, actionId: number): SelectionMap => {
      const next = new Map(prev);
      next.set(actionId, !(prev.get(actionId) ?? true));
      return next;
    },
    []
  );

  const toggleAction = useCallback(
    (actionId: number): number[] => {
      setSelection(prev => withToggled(prev, actionId));
      // Return the new selected IDs — computed synchronously from the closure
      // state for callers that need them before React commits.
      return selectedIdsOf(withToggled(selection, actionId));
    },
    [selection, withToggled]
  );

  const selectActions = useCallback((actionIds: number[]): void => {
    setSelection(prev => {
      const next = new Map(prev);
      for (const actionId of actionIds) {
        next.set(actionId, true);
      }
      return next;
    });
  }, []);

  const deselectActions = useCallback((actionIds: number[]): void => {
    setSelection(prev => {
      const next = new Map(prev);
      for (const actionId of actionIds) {
        next.set(actionId, false);
      }
      return next;
    });
  }, []);

  // Compute selected action IDs
  const selectedActionIds = useMemo(
    () => selectedIdsOf(selection),
    [selection]
  );

  // Reset to initial state
  const resetSelection = useCallback(() => {
    setSelection(initialSelection);
  }, [initialSelection]);

  // Set selection from array of IDs
  const setSelectionFromIds = useCallback(
    (actionIds: number[]) => {
      const idsSet = new Set(actionIds);
      const newSelection = new Map<number, boolean>();
      for (const action of actions) {
        newSelection.set(action.id, idsSet.has(action.id));
      }
      setSelection(newSelection);
    },
    [actions]
  );

  // Check if all selectable actions are selected
  const isAllSelected = useMemo(() => {
    if (selectableActions.length === 0) {
      return false;
    }
    return selectableActions.every(action => selection.get(action.id) === true);
  }, [selectableActions, selection]);

  // Check if some (but not all) selectable actions are selected
  const isSomeSelected = useMemo(() => {
    const selectedCount = selectableActions.filter(
      action => selection.get(action.id) === true
    ).length;
    return selectedCount > 0 && selectedCount < selectableActions.length;
  }, [selectableActions, selection]);

  // Select all actions (non-selectable actions, e.g. no-ops, stay deselected)
  const selectAll = useCallback(() => {
    const newSelection = new Map<number, boolean>();
    for (const action of actions) {
      newSelection.set(action.id, !nonSelectableSet.has(action.id));
    }
    setSelection(newSelection);
  }, [actions, nonSelectableSet]);

  // Deselect all actions
  const deselectAll = useCallback(() => {
    const newSelection = new Map<number, boolean>();
    for (const action of actions) {
      newSelection.set(action.id, false);
    }
    setSelection(newSelection);
  }, [actions]);

  return {
    isSelected,
    toggleAction,
    deselectActions,
    selectActions,
    selectedActionIds,
    resetSelection,
    setSelection: setSelectionFromIds,
    selectAll,
    deselectAll,
    isAllSelected,
    isSomeSelected,
  };
}
